// services/workflow.service.js
import AuthService from './auth.service.js'
import logger from '../utils/logger';

/**
 * Workflow Service
 * Maneja todas las operaciones relacionadas con Workflows
 * Base URL configured via REACT_APP_WORKFLOW_API_URL
 * Execution API configured via REACT_APP_EXECUTION_API_URL
 */

const WORKFLOW_API_URL = process.env.REACT_APP_WORKFLOW_API_URL || 'http://localhost:8000';
const EXECUTION_API_URL = process.env.REACT_APP_EXECUTION_API_URL || 'https://run-dev.zenitailab.com';

class WorkflowService {
  constructor() {
    // Override the base URL for workflow API
    this.baseURL = WORKFLOW_API_URL;
    this.executionURL = EXECUTION_API_URL;
  }

  generateRequestId() {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    return `req-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
  }

  getFallbackApiKey() {
    const envApiKey = process.env.REACT_APP_ZENIT_API_KEY || process.env.REACT_APP_API_KEY || null;

    if (typeof window === 'undefined') {
      return envApiKey;
    }

    try {
      const runtimeApiKey =
        localStorage.getItem('zenit_api_key') ||
        localStorage.getItem('api_key') ||
        localStorage.getItem('temp_api_key');

      return runtimeApiKey || envApiKey;
    } catch (_) {
      return envApiKey;
    }
  }

  /**
   * Realizar request HTTP específico para workflow API con JWT
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} options - Opciones del fetch
   * @returns {Promise<Object>} Respuesta de la API
   */
  async request(endpoint, options = {}) {
    // Use the full URL with workflow API base
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    
    // Get JWT token
    let token = AuthService.getAccessToken();
    
    // Check if token is expired
    if (token && AuthService.isTokenExpired()) {
      logger.warn('JWT token is expired. User needs to re-authenticate.');
      AuthService.logout();
      token = null;
    }
    
    // Prepare headers with JWT
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };
    
    // Add JWT if available and not expired, otherwise fallback to API key.
    if (token && !AuthService.isTokenExpired()) {
      headers.Authorization = `Bearer ${token}`;
    } else {
      const apiKey = this.getFallbackApiKey();
      if (apiKey && !headers['X-Zenit-Api-Key'] && !headers['x-zenit-api-key']) {
        headers['X-Zenit-Api-Key'] = apiKey;
      } else {
        logger.debug('No JWT/API key available for workflow API request');
      }
    }
    
    const config = {
      ...options,
      headers,
    };

    try {
      logger.debug(`Workflow API Request: ${config.method || 'GET'} ${url}`);
      
      const response = await fetch(url, config);
      
      // Handle authentication errors
      if (response.status === 401) {
        if (!token) {
          throw new Error('Unauthorized request to workflow API');
        }

        logger.warn('Token expired or invalid for workflow API');
        AuthService.logout();
        
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        
        throw new Error('Session expired - redirecting to login');
      }

      // Parse JSON response
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = { message: await response.text() };
      }
      
      // Handle HTTP errors
      if (!response.ok) {
        // FastAPI-style errors place the structured payload under `detail`.
        const detail = data?.detail;
        const detailMessage = typeof detail === 'string'
          ? detail
          : (detail?.message || detail?.error || null);
        const detailCode = typeof detail === 'object' && detail ? detail.code : null;
        const errorMessage = detailMessage || data.message || data.error || `HTTP Error ${response.status}`;
        const err = new Error(errorMessage);
        err.status = response.status;
        if (detailCode) err.code = detailCode;
        throw err;
      }

      // Some backend environments can return HTTP 2xx with an error wrapper.
      const wrappedStatusCode = Number(data?.status_code);
      if (Number.isFinite(wrappedStatusCode) && wrappedStatusCode >= 400) {
        throw new Error(data?.message || `Workflow API Error ${wrappedStatusCode}`);
      }

      logger.api(config.method || 'GET', url, response.status);
      return data;
      
    } catch (error) {
      // Don't log expected 404s as errors — callers handle them gracefully
      if (error.status !== 404) {
        logger.error('Workflow API Error:', error.message);
      }
      throw error;
    }
  }

  /**
   * Realizar request HTTP específico para Execution API con JWT
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} options - Opciones del fetch
   * @returns {Promise<Object>} Respuesta de la API
   */
  async executionRequest(endpoint, options = {}) {
    // Use the full URL with execution API base
    const url = endpoint.startsWith('http') ? endpoint : `${this.executionURL}${endpoint}`;
    
    // Get JWT token
    let token = AuthService.getAccessToken();
    
    // Check if token is expired
    if (token && AuthService.isTokenExpired()) {
      logger.warn('JWT token is expired. User needs to re-authenticate.');
      AuthService.logout();
      token = null;
    }
    
    // Prepare headers with JWT
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };
    
    // Add JWT if available and not expired, otherwise fallback to API key.
    if (token && !AuthService.isTokenExpired()) {
      headers.Authorization = `Bearer ${token}`;
    } else {
      const apiKey = this.getFallbackApiKey();
      if (apiKey && !headers['X-Zenit-Api-Key'] && !headers['x-zenit-api-key']) {
        headers['X-Zenit-Api-Key'] = apiKey;
      } else {
        logger.debug('No JWT/API key available for execution API request');
      }
    }
    
    const config = {
      ...options,
      headers,
    };

    try {
      logger.debug(`Execution API Request: ${config.method || 'GET'} ${url}`);
      
      const response = await fetch(url, config);
      
      // Handle authentication errors
      if (response.status === 401) {
        if (!token) {
          throw new Error('Unauthorized request to execution API');
        }

        logger.warn('Token expired or invalid for execution API');
        AuthService.logout();
        
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        
        throw new Error('Session expired - redirecting to login');
      }

      // Parse JSON response
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = { message: await response.text() };
      }
      
      // Handle HTTP errors
      if (!response.ok) {
        const errorMessage = data.message || data.error || `HTTP Error ${response.status}`;
        throw new Error(errorMessage);
      }

      // Some backend environments can return HTTP 2xx with an error wrapper.
      const wrappedStatusCode = Number(data?.status_code);
      if (Number.isFinite(wrappedStatusCode) && wrappedStatusCode >= 400) {
        throw new Error(data?.message || `Execution API Error ${wrappedStatusCode}`);
      }

      logger.api(config.method || 'GET', url, response.status);
      return data;
      
    } catch (error) {
      logger.error('Execution API Error:', error.message);
      throw error;
    }
  }

  /**
   * List workflows with filtering and pagination - Protected endpoint
   * GET /workflows
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @param {string} [params.status] - Status filter
   * @param {number} [params.limit=100] - Limit for pagination
   * @param {number} [params.offset=0] - Offset for pagination
   * @param {string} [params.sort_by='updated_at'] - Sort by field
   * @param {boolean} [params.descending=true] - Sort order
   * @param {boolean} [params.include_definition=false] - Include workflow definition
   * @param {boolean} [params.include_soft_deleted=false] - Include soft deleted workflows
   * @returns {Promise<Object>} response { success, data: { workflows: [...], total_count, has_more }, error }
   */
  async listWorkflows({
    account_id,
    project_id,
    status = null,
    limit = 100,
    offset = 0,
    sort_by = 'updated_at',
    descending = true,
    include_definition = false,
    include_soft_deleted = false
  }) {
    try {
      if (!account_id) {
        return {
          success: false,
          error: 'account_id is required'
        };
      }

      if (!project_id) {
        return {
          success: false,
          error: 'project_id is required'
        };
      }

      const params = new URLSearchParams({
        account_id,
        project_id,
        limit: limit.toString(),
        offset: offset.toString(),
        sort_by,
        descending: descending.toString(),
        include_definition: include_definition.toString(),
        include_soft_deleted: include_soft_deleted.toString()
      });

      if (status) {
        params.append('status', status);
      }

      const response = await this.request(`/workflows/?${params}`);
      
      return {
        success: true,
        data: {
          workflows: response.body?.workflows || [],
          total_count: response.body?.total_count || 0,
          has_more: response.body?.has_more || false,
          request_id: response.body?.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing workflows:', error);
      
      // Handle specific error types
      let errorMessage = 'Error listing workflows';
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        errorMessage = 'Unable to connect to workflow service. Please check your connection or try again later.';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'CORS error - workflow service may not be properly configured.';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Per-day workflow-run activity for the heatmap (server-aggregated).
   * GET /workflows/activity
   * @param {Object} params
   * @param {string} params.account_id - Account ID (required)
   * @param {string} [params.project_id] - Scope to a project (account-wide if omitted)
   * @param {string} [params.since] - Window start (UTC ISO-8601)
   * @param {string} [params.until] - Window end (UTC ISO-8601)
   * @returns {Promise<Object>} { success, data: { buckets:[{date,count,succeeded,failed}], total_runs, succeeded, failed, active_days, since, until }, error }
   */
  async getWorkflowActivity({ account_id, project_id, since, until, include_samples, samples_per_day } = {}) {
    try {
      if (!account_id) return { success: false, error: 'account_id is required' };

      const params = new URLSearchParams({ account_id });
      if (project_id) params.append('project_id', project_id);
      if (since) params.append('since', since);
      if (until) params.append('until', until);
      if (include_samples) params.append('include_samples', 'true');
      if (samples_per_day) params.append('samples_per_day', String(samples_per_day));

      const response = await this.request(`/workflows/activity?${params}`);
      const activity = response.body?.activity || {};
      return {
        success: true,
        data: {
          buckets: activity.buckets || [],
          // La API responde camelCase (`totalRuns`, `activeDays`). Leerlos en
          // snake_case daba 0 siempre: la cabecera decía "0 active days" sobre
          // un año con 23 días de actividad y 629 corridas. Se acepta la forma
          // vieja por si algún ambiente todavía la manda.
          total_runs: activity.totalRuns ?? activity.total_runs ?? 0,
          succeeded: activity.succeeded ?? 0,
          failed: activity.failed ?? 0,
          active_days: activity.activeDays ?? activity.active_days ?? 0,
          since: activity.since,
          until: activity.until,
        },
      };
    } catch (error) {
      console.error('WorkflowService: Error getting workflow activity:', error);
      return { success: false, error: error.message || 'Error getting workflow activity' };
    }
  }

  /**
   * Agents participating in a project's workflows (Epic 32 index, project-scoped).
   * GET /workflows/agents
   * Returns the assistants referenced by the project's workflows — which may live
   * in a different project than the workflow, so this catches agents that the
   * project's own assistant list misses.
   * @param {Object} params
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @param {string} [params.source='published'] - 'draft' | 'published'
   * @returns {Promise<Object>} { success, data: { agents: [{id, name, avatar}] }, error }
   */
  async getProjectAgents({ account_id, project_id, source = 'published' } = {}) {
    try {
      if (!account_id) return { success: false, error: 'account_id is required' };
      if (!project_id) return { success: false, error: 'project_id is required' };

      const params = new URLSearchParams({ account_id, project_id, source });
      const response = await this.request(`/workflows/agents?${params}`);
      return {
        success: true,
        data: { agents: response.body?.agents || [] },
      };
    } catch (error) {
      console.error('WorkflowService: Error getting project agents:', error);
      return { success: false, error: error.message || 'Error getting project agents' };
    }
  }

  /**
   * Get a specific workflow by ID - Protected endpoint
   * GET /workflows/{workflow_id}
   * @param {string} workflow_id - Workflow ID (required, UUID format)
   * @param {string} [project_id] - Project ID (optional)
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async getWorkflow(workflow_id, project_id = null) {
    try {
      if (!workflow_id) {
        return {
          success: false,
          error: 'workflow_id is required'
        };
      }

      const params = new URLSearchParams();
      if (project_id) {
        params.append('project_id', project_id);
      }

      const queryString = params.toString();
      const endpoint = `/workflows/${workflow_id}${queryString ? `?${queryString}` : ''}`;

      const response = await this.request(endpoint);
      
      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          steps: response.body?.steps || [],
          triggers: response.body?.triggers || [],
          edges: response.body?.workflow?.canvas_edges || response.body?.canvas_edges || response.body?.edges || response.canvas_edges || response.edges || [],
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting workflow:', error);
      return {
        success: false,
        error: error.message || 'Error getting workflow'
      };
    }
  }

  /**
   * Create a new workflow - Protected endpoint
   * POST /workflows/
   * @param {Object} workflowData - Workflow data
   * @param {string} workflowData.account_id - Account ID (required)
   * @param {string} workflowData.project_id - Project ID (required)
   * @param {string} workflowData.name - Workflow name (required)
   * @param {string} [workflowData.description] - Workflow description
   * @param {Object} [workflowData.definition] - Workflow definition
   * @param {Array} [workflowData.tags] - Tags for the workflow
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async createWorkflow(workflowData) {
    try {
      if (!workflowData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!workflowData.project_id) {
        return { success: false, error: 'project_id is required' };
      }
      if (!workflowData.name) {
        return { success: false, error: 'name is required' };
      }

      const response = await this.request('/workflows/', {
        method: 'POST',
        body: JSON.stringify(this.sanitizeWorkflowData(workflowData))
      });

      const wrappedStatusCode = Number(response.status_code);
      const wrappedErrorMessage =
        Number.isFinite(wrappedStatusCode) && wrappedStatusCode >= 400
          ? (response.message || 'Error creating workflow')
          : null;

      const normalizeWorkflowId = (value) =>
        typeof value === 'string' && this.isValidUUID(value) ? value : null;

      let resolvedWorkflow = response.body?.workflow || response.workflow || null;
      let resolvedWorkflowId =
        normalizeWorkflowId(resolvedWorkflow?.id) ||
        normalizeWorkflowId(resolvedWorkflow?.workflow_id) ||
        normalizeWorkflowId(response.body?.workflow_id) ||
        normalizeWorkflowId(response.workflow_id) ||
        normalizeWorkflowId(response.id);

      // Some environments return 201 with workflow=null. Recover by listing recent workflows.
      if (!resolvedWorkflowId && workflowData.account_id && workflowData.project_id) {
        const listResult = await this.listWorkflows({
          account_id: workflowData.account_id,
          project_id: workflowData.project_id,
          limit: 25,
          offset: 0,
          sort_by: 'updated_at',
          descending: true,
          include_definition: false
        });

        if (listResult.success && Array.isArray(listResult.data?.workflows)) {
          const workflows = listResult.data.workflows;
          const now = Date.now();
          const nameCandidates = workflows.filter((wf) => wf?.name === workflowData.name);
          const pool = nameCandidates.length > 0 ? nameCandidates : workflows;

          const recentCandidate = pool.find((wf) => {
            const timestamp = wf?.updated_at || wf?.created_at;
            if (!timestamp) return false;
            const ageMs = Math.abs(now - new Date(timestamp).getTime());
            return Number.isFinite(ageMs) && ageMs <= 10 * 60 * 1000;
          });

          const fallbackWorkflow = recentCandidate || pool[0] || null;
          if (fallbackWorkflow) {
            resolvedWorkflow = fallbackWorkflow;
            resolvedWorkflowId =
              normalizeWorkflowId(fallbackWorkflow.id) ||
              normalizeWorkflowId(fallbackWorkflow.workflow_id) ||
              resolvedWorkflowId;
          }
        }
      }

      if (!resolvedWorkflowId) {
        return {
          success: false,
          error: wrappedErrorMessage || response.message || 'Workflow created but no valid workflow_id was returned'
        };
      }

      return {
        success: true,
        data: {
          workflow: resolvedWorkflow || response.body?.workflow || response.workflow || response,
          workflow_id: resolvedWorkflowId,
          request_id: response.body?.request_id || response.request_id
        },
        warning: wrappedErrorMessage
      };
    } catch (error) {
      console.error('WorkflowService: Error creating workflow:', error);
      return {
        success: false,
        error: error.message || 'Error creating workflow'
      };
    }
  }

  /**
   * Update an existing workflow - Protected endpoint
   * PUT /workflows/{workflow_id}
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} workflowData - Workflow data to update
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async updateWorkflow(workflow_id, workflowData) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const payload = {
        ...(workflowData || {}),
        workflow_id: workflowData?.workflow_id || workflow_id
      };

      const response = await this.request(`/workflows/${workflow_id}`, {
        method: 'PUT',
        body: JSON.stringify(this.sanitizeWorkflowData(payload))
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error updating workflow:', error);
      return {
        success: false,
        error: error.message || 'Error updating workflow'
      };
    }
  }

  /**
   * Delete a workflow (soft delete) - Protected endpoint
   * DELETE /workflows/{workflow_id}
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} params - { account_id, project_id, user_id }
   * @returns {Promise<Object>} response { success, error }
   */
  async deleteWorkflow(workflow_id, { account_id, project_id, user_id } = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const payload = {
        workflow_id,
        hard_delete: false,
        account_id,
        project_id,
        user_id
      };

      await this.request(`/workflows/${workflow_id}`, {
        method: 'DELETE',
        body: JSON.stringify(payload)
      });

      return { success: true };
    } catch (error) {
      console.error('WorkflowService: Error deleting workflow:', error);
      return {
        success: false,
        error: error.message || 'Error deleting workflow'
      };
    }
  }

  /**
   * Validate workflow definition - Protected endpoint
   * POST /workflows/{workflow_id}/validate
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} workflowData - Workflow data to validate (optional)
   * @returns {Promise<Object>} response { success, data: { valid, errors }, error }
   */
  async validateWorkflow(workflow_id, workflowData = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const payload = {
        ...workflowData,
        workflow_id: workflow_id
      };

      const response = await this.request(`/workflows/${workflow_id}/validate`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          valid: response.body?.valid ?? response.valid ?? true,
          errors: response.body?.errors || response.errors || [],
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error validating workflow:', error);
      return {
        success: false,
        error: error.message || 'Error validating workflow'
      };
    }
  }

  /**
   * Activate workflow for execution - Protected endpoint
   * POST /workflows/{workflow_id}/activate
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} activateData - Activation data
   * @param {string} activateData.account_id - Account ID (required)
   * @param {string} activateData.project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async activateWorkflow(workflow_id, activateData = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!activateData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!activateData.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const payload = {
        account_id: activateData.account_id,
        project_id: activateData.project_id,
        workflow_id: workflow_id
      };

      const response = await this.request(`/workflows/${workflow_id}/activate`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error activating workflow:', error);
      return {
        success: false,
        error: error.message || 'Error activating workflow'
      };
    }
  }

  /**
   * Deactivate workflow - Protected endpoint
   * POST /workflows/{workflow_id}/deactivate
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} deactivateData - Deactivation data
   * @param {string} deactivateData.account_id - Account ID (required)
   * @param {string} deactivateData.project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async deactivateWorkflow(workflow_id, deactivateData = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!deactivateData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!deactivateData.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const payload = {
        account_id: deactivateData.account_id,
        project_id: deactivateData.project_id,
        workflow_id: workflow_id
      };

      const response = await this.request(`/workflows/${workflow_id}/deactivate`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error deactivating workflow:', error);
      return {
        success: false,
        error: error.message || 'Error deactivating workflow'
      };
    }
  }

  /**
   * List agents assigned to a workflow - GET /workflows/{workflow_id}/agents
   * @param {string} workflow_id
   * @param {Object} params - { source: 'draft' | 'published' }
   * @returns {Promise<Object>} { success, data: { workflow_id, source, agent_ids }, error }
   */
  async listWorkflowAgents(workflow_id, { source = 'draft' } = {}) {
    try {
      if (!workflow_id) return { success: false, error: 'workflow_id is required' };

      const params = new URLSearchParams({ source });
      const response = await this.request(`/workflows/${workflow_id}/agents?${params}`);

      return {
        success: true,
        data: {
          workflow_id: response.body?.workflow_id || workflow_id,
          source: response.body?.source || source,
          agent_ids: response.body?.agent_ids || []
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing workflow agents:', error);
      return { success: false, error: error.message || 'Error listing workflow agents' };
    }
  }

  /**
   * Restore a soft-deleted workflow - Protected endpoint
   * POST /workflows/{workflow_id}/restore
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} params - { account_id, project_id, user_id }
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async restoreWorkflow(workflow_id, { account_id, project_id } = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const payload = { workflow_id, account_id, project_id };

      const response = await this.request(`/workflows/${workflow_id}/restore`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error restoring workflow:', error);
      return {
        success: false,
        error: error.message || 'Error restoring workflow'
      };
    }
  }

  /**
   * Clean expired workflows (Admin only) - Protected endpoint
   * POST /workflows/admin/clean-expired
   * @returns {Promise<Object>} response { success, data: { cleaned_count }, error }
   */
  async cleanExpiredWorkflows() {
    try {
      const response = await this.request('/workflows/admin/clean-expired', {
        method: 'POST'
      });

      return {
        success: true,
        data: {
          cleaned_count: response.body?.cleaned_count || response.cleaned_count || 0,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error cleaning expired workflows:', error);
      return {
        success: false,
        error: error.message || 'Error cleaning expired workflows'
      };
    }
  }

  /**
   * Helper method to validate UUID format
   * @param {string} uuid - UUID string to validate
   * @returns {boolean} true if valid UUID format
   */
  isValidUUID(uuid) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  /**
   * Helper method to sanitize workflow data
   * @param {Object} workflow - Workflow data
   * @returns {Object} Sanitized workflow data
   */
  sanitizeWorkflowData(workflow) {
    if (!workflow) return workflow;

    // Basic sanitization - remove any unwanted fields
    const sanitized = { ...workflow };

    // Ensure proper data types
    if (sanitized.timeout_seconds && typeof sanitized.timeout_seconds !== 'number') {
      sanitized.timeout_seconds = parseInt(sanitized.timeout_seconds, 10);
    }

    if (sanitized.tags && !Array.isArray(sanitized.tags)) {
      sanitized.tags = [];
    }

    if (sanitized.steps && !Array.isArray(sanitized.steps)) {
      sanitized.steps = [];
    }

    if (sanitized.edges && !Array.isArray(sanitized.edges)) {
      sanitized.edges = [];
    }

    return sanitized;
  }

  /**
   * Helper method to format workflow status for display
   * @param {string} status - Workflow status
   * @returns {Object} { label, color, bgColor }
   */
  formatStatus(status) {
    const statusMap = {
      'draft': {
        label: 'Draft',
        color: 'text-yellow-400',
        bgColor: 'bg-yellow-900/30'
      },
      'active': {
        label: 'Active',
        color: 'text-green-400',
        bgColor: 'bg-green-900/30'
      },
      'disabled': {
        label: 'Disabled',
        color: 'text-orange-400',
        bgColor: 'bg-orange-900/30'
      },
      'archived': {
        label: 'Archived',
        color: 'text-gray-400',
        bgColor: 'bg-gray-900/30'
      }
    };

    return statusMap[status] || {
      label: status || 'Unknown',
      color: 'text-gray-400',
      bgColor: 'bg-gray-900/30'
    };
  }

  /**
   * Helper method to format execution mode for display
   * @param {string} executionMode - Execution mode
   * @returns {string} Formatted execution mode
   */
  formatExecutionMode(executionMode) {
    // Workflow-specific execution modes
    const modeMap = {
      'sequential': 'Sequential',
      'parallel': 'Parallel',
      'conditional': 'Conditional'
    };

    // Also try assistant execution mode labels as fallback
    try {
      const { getExecutionModeLabel } = require('../types/assistant.types');
      const label = getExecutionModeLabel(executionMode);
      if (label && label !== executionMode && label !== 'Unknown') return label;
    } catch (_) { /* ignore */ }

    return modeMap[executionMode] || executionMode || 'Unknown';
  }

  // ==========================================
  // TRIGGERS API
  // ==========================================

  /**
   * Create a new trigger - Protected endpoint
   * POST /triggers/
   * @param {Object} triggerData - Trigger data
   * @param {string} triggerData.workflow_id - Workflow ID (required)
   * @param {string} triggerData.trigger_type - Type: manual | scheduled | webhook | shopify | whatsapp
   * @param {string} [triggerData.name] - Trigger name
   * @param {Object} [triggerData.config] - Trigger configuration
   * @returns {Promise<Object>} response { success, data: { trigger }, error }
   */
  async createTrigger(triggerData) {
    try {
      if (!triggerData.workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!triggerData.account_id) {
        return { success: false, error: 'account_id is required' };
      }

      const baseConfiguration = triggerData.configuration || {};
      const normalizedConfiguration = {
        ...baseConfiguration
      };

      // Backward compatibility: allow callers that still send trigger_type on top-level.
      if (!normalizedConfiguration.trigger_type && triggerData.trigger_type) {
        normalizedConfiguration.trigger_type = triggerData.trigger_type;
      }

      if (!normalizedConfiguration || typeof normalizedConfiguration !== 'object' || Array.isArray(normalizedConfiguration)) {
        return { success: false, error: 'configuration is required' };
      }

      const payload = {
        id: triggerData.id || this.generateRequestId(),
        workflow_id: triggerData.workflow_id,
        account_id: triggerData.account_id,
        project_id: triggerData.project_id || null,
        configuration: normalizedConfiguration,
        is_active: triggerData.is_active !== false,
        position: triggerData.position || null
      };

      let response;
      try {
        response = await this.request('/triggers/', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      } catch (primaryError) {
        const message = String(primaryError?.message || '');
        if (!message.includes('404')) {
          throw primaryError;
        }

        const isBrowser = typeof window !== 'undefined';
        if (isBrowser) {
          // In browser, retrying without trailing slash can trigger cross-origin redirects
          // (e.g. https -> http), which fails CORS preflight and masks the original 404.
          throw primaryError;
        }

        // Fallback for non-browser environments with strict slash handling.
        response = await this.request('/triggers', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      return {
        success: true,
        data: {
          trigger: response.body?.trigger || response.trigger || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error creating trigger:', error);
      return {
        success: false,
        error: error.message || 'Error creating trigger'
      };
    }
  }

  /**
   * Get a specific trigger by ID - Protected endpoint
   * GET /triggers/{trigger_id}
   * @param {string} trigger_id - Trigger ID (required)
   * @returns {Promise<Object>} response { success, data: { trigger }, error }
   */
  async getTrigger(trigger_id) {
    try {
      if (!trigger_id) {
        return { success: false, error: 'trigger_id is required' };
      }

      const response = await this.request(`/triggers/${trigger_id}`);

      return {
        success: true,
        data: {
          trigger: response.body?.trigger || response.trigger || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting trigger:', error);
      return {
        success: false,
        error: error.message || 'Error getting trigger'
      };
    }
  }

  /**
   * List triggers with filters - Protected endpoint
   * GET /triggers/
   * @param {Object} params - Query parameters
   * @param {string} [params.workflow_id] - Filter by workflow ID
   * @param {string} [params.trigger_type] - Filter by trigger type
   * @param {boolean} [params.is_active] - Filter by active status
   * @param {number} [params.limit=100] - Limit for pagination
   * @param {number} [params.offset=0] - Offset for pagination
   * @returns {Promise<Object>} response { success, data: { triggers, total_count }, error }
   */
  async listTriggers({
    account_id = null,
    workflow_id = null,
    trigger_type = null,
    is_active = null,
    limit = 100,
    offset = 0
  } = {}) {
    try {
      if (!account_id) {
        return { success: false, error: 'account_id is required' };
      }

      const params = new URLSearchParams({
        account_id,
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (workflow_id) params.append('workflow_id', workflow_id);
      if (trigger_type) params.append('trigger_type', trigger_type);
      if (is_active !== null) params.append('is_active', is_active.toString());

      const response = await this.request(`/triggers/?${params}`);

      return {
        success: true,
        data: {
          triggers: response.body?.triggers || response.triggers || [],
          total_count: response.body?.total_count || response.total_count || 0,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing triggers:', error);
      return {
        success: false,
        error: error.message || 'Error listing triggers'
      };
    }
  }

  /**
   * List triggers for a specific workflow - Protected endpoint
   * GET /triggers/workflow/{workflow_id}
   * @param {string} workflow_id - Workflow ID (required)
   * @returns {Promise<Object>} response { success, data: { triggers }, error }
   */
  async listTriggersByWorkflow(workflow_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const response = await this.request(`/triggers/workflow/${workflow_id}`);

      return {
        success: true,
        data: {
          triggers: response.body?.triggers || response.triggers || [],
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing triggers by workflow:', error);
      return {
        success: false,
        error: error.message || 'Error listing triggers'
      };
    }
  }

  /**
   * Update a trigger - Protected endpoint
   * PUT /triggers/{trigger_id}
   * @param {string} trigger_id - Trigger ID (required)
   * @param {Object} triggerData - Trigger data to update
   * @returns {Promise<Object>} response { success, data: { trigger }, error }
   */
  async updateTrigger(trigger_id, triggerData) {
    try {
      if (!trigger_id) {
        return { success: false, error: 'trigger_id is required' };
      }

      // Extract authorization/routing fields for query params
      const workflow_id = triggerData?.workflow_id;
      const account_id = triggerData?.account_id;
      const project_id = triggerData?.project_id;

      // Build body payload with only fields the backend accepts
      const bodyPayload = {};
      if (triggerData.configuration !== undefined) bodyPayload.configuration = triggerData.configuration;
      if (triggerData.is_active !== undefined) bodyPayload.is_active = triggerData.is_active;
      if (triggerData.name !== undefined) bodyPayload.name = triggerData.name;
      if (triggerData.position !== undefined) bodyPayload.position = triggerData.position;

      // Build query string for authorization
      const queryParams = new URLSearchParams();
      if (workflow_id) queryParams.append('workflow_id', workflow_id);
      if (account_id) queryParams.append('account_id', account_id);
      if (project_id) queryParams.append('project_id', project_id);
      const queryString = queryParams.toString();

      const response = await this.request(`/triggers/${trigger_id}${queryString ? `?${queryString}` : ''}`, {
        method: 'PUT',
        body: JSON.stringify(bodyPayload)
      });

      return {
        success: true,
        data: {
          trigger: response.body?.trigger || response.trigger || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error updating trigger:', error);
      return {
        success: false,
        error: error.message || 'Error updating trigger'
      };
    }
  }

  /**
   * Delete a trigger - Protected endpoint
   * DELETE /triggers/{trigger_id}
   * @param {string} trigger_id - Trigger ID (required)
   * @returns {Promise<Object>} response { success, error }
   */
  async deleteTrigger(trigger_id) {
    try {
      if (!trigger_id) {
        return { success: false, error: 'trigger_id is required' };
      }

      await this.request(`/triggers/${trigger_id}`, {
        method: 'DELETE'
      });

      return { success: true };
    } catch (error) {
      console.error('WorkflowService: Error deleting trigger:', error);
      return {
        success: false,
        error: error.message || 'Error deleting trigger'
      };
    }
  }

  /**
   * Get triggers for a specific workflow - Protected endpoint
   * GET /triggers/workflow/{workflow_id}
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} params - Query parameters
   * @param {boolean} [params.is_active] - Filter by active status (optional)
   * @param {number} [params.limit=100] - Limit for pagination
   * @param {number} [params.offset=0] - Offset for pagination
   * @returns {Promise<Object>} response { success, data: { triggers, total_count }, error }
   */
  async getWorkflowTriggers(workflow_id, params = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!params.account_id) {
        return { success: false, error: 'account_id is required' };
      }

      const queryParams = new URLSearchParams();
      queryParams.append('account_id', params.account_id);
      queryParams.append('workflow_id', workflow_id);
      if (params.is_active !== undefined) {
        queryParams.append('is_active', params.is_active);
      }
      if (params.limit !== undefined) {
        queryParams.append('limit', params.limit);
      } else {
        queryParams.append('limit', '100');
      }
      if (params.offset !== undefined) {
        queryParams.append('offset', params.offset);
      }

      const queryString = queryParams.toString();
      let response;
      try {
        response = await this.request(`/triggers/?${queryString}`);
      } catch (primaryError) {
        // Endpoint no disponible — el caller usará los IDs del caché local
        return { success: false, error: 'Trigger list endpoint not available' };
      }

      return {
        success: true,
        data: {
          triggers: response.body?.triggers || response.triggers || [],
          total_count: response.body?.total_count || response.total_count || 0,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      if (String(error?.message || '').includes('404')) {
        return { success: false, error: 'Trigger list endpoint not available' };
      }
      console.error('WorkflowService: Error getting workflow triggers:', error);
      return {
        success: false,
        error: error.message || 'Error getting workflow triggers'
      };
    }
  }

  /**
   * Activate a trigger - Protected endpoint
   * POST /triggers/{trigger_id}/activate
   * @param {string} trigger_id - Trigger ID (required)
   * @returns {Promise<Object>} response { success, data: { trigger }, error }
   */
  async activateTrigger(trigger_id) {
    try {
      if (!trigger_id) {
        return { success: false, error: 'trigger_id is required' };
      }

      const response = await this.request(`/triggers/${trigger_id}/activate`, {
        method: 'POST'
      });

      return {
        success: true,
        data: {
          trigger: response.body?.trigger || response.trigger || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error activating trigger:', error);
      return {
        success: false,
        error: error.message || 'Error activating trigger'
      };
    }
  }

  /**
   * Deactivate a trigger - Protected endpoint
   * POST /triggers/{trigger_id}/deactivate
   * @param {string} trigger_id - Trigger ID (required)
   * @returns {Promise<Object>} response { success, data: { trigger }, error }
   */
  async deactivateTrigger(trigger_id) {
    try {
      if (!trigger_id) {
        return { success: false, error: 'trigger_id is required' };
      }

      const response = await this.request(`/triggers/${trigger_id}/deactivate`, {
        method: 'POST'
      });

      return {
        success: true,
        data: {
          trigger: response.body?.trigger || response.trigger || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error deactivating trigger:', error);
      return {
        success: false,
        error: error.message || 'Error deactivating trigger'
      };
    }
  }

  /**
   * List Shopify triggers - Protected endpoint
   * GET /triggers/shopify/list
   * @param {Object} params - Query parameters
   * @param {string} [params.shop_domain] - Shopify shop domain
   * @param {string} [params.event_type] - Event type (orders/create, products/update, etc.)
   * @param {boolean} [params.is_active] - Filter by active status
   * @param {number} [params.limit=100] - Limit for pagination
   * @param {number} [params.offset=0] - Offset for pagination
   * @returns {Promise<Object>} response { success, data: { triggers, total_count }, error }
   */
  async listShopifyTriggers({
    shop_domain = null,
    event_type = null,
    is_active = null,
    limit = 100,
    offset = 0
  } = {}) {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (shop_domain) params.append('shop_domain', shop_domain);
      if (event_type) params.append('event_type', event_type);
      if (is_active !== null) params.append('is_active', is_active.toString());

      const response = await this.request(`/triggers/shopify/list?${params}`);

      return {
        success: true,
        data: {
          triggers: response.body?.triggers || response.triggers || [],
          total_count: response.body?.total_count || response.total_count || 0,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing Shopify triggers:', error);
      return {
        success: false,
        error: error.message || 'Error listing Shopify triggers'
      };
    }
  }

  // ============================================================================
  // TRIGGER CATALOG ENDPOINTS
  // ============================================================================

  /**
   * List every registered trigger type with metadata and JSON schema
   * GET /triggers/catalog
   * @returns {Promise<Object>} { success, data: { entries, total, categories }, error }
   */
  async getTriggerCatalog() {
    try {
      const response = await this.request('/triggers/catalog');
      return {
        success: true,
        data: {
          entries: response.body?.entries || response.entries || [],
          total: response.body?.total ?? response.total ?? 0,
          categories: response.body?.categories || response.categories || []
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error fetching trigger catalog:', error);
      return {
        success: false,
        error: error.message || 'Error fetching trigger catalog'
      };
    }
  }

  /**
   * Get a single trigger type by its identifier
   * GET /triggers/catalog/{trigger_type}
   * @param {string} triggerType - The trigger type identifier (e.g. 'webhook', 'scheduled')
   * @returns {Promise<Object>} { success, data: entry, error }
   */
  async getTriggerCatalogEntry(triggerType) {
    try {
      const response = await this.request(`/triggers/catalog/${triggerType}`);
      return {
        success: true,
        data: response.body || response
      };
    } catch (error) {
      console.error(`WorkflowService: Error fetching trigger catalog entry for '${triggerType}':`, error);
      return {
        success: false,
        error: error.message || `Trigger type '${triggerType}' not found`
      };
    }
  }

  // ============================================================================
  // EXECUTION ENDPOINTS
  // ============================================================================

  /**
   * Start a workflow execution
   * POST /executions/
   * @param {Object} executionData - Execution parameters
   * @param {string} executionData.account_id - Account ID (required)
   * @param {string} executionData.project_id - Project ID (required)
   * @param {string} executionData.workflow_id - Workflow ID to execute (required)
   * @param {Object} [executionData.input_parameters] - Input parameters for execution
   * @param {string} [executionData.triggered_by_user_id] - User who triggered
   * @param {Object} [executionData.metadata] - Optional execution metadata
   * @returns {Promise<Object>} response { success, data: { execution }, error }
   */
  async startExecution(executionData) {
    try {
      if (!executionData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!executionData.project_id) {
        return { success: false, error: 'project_id is required' };
      }
      if (!executionData.workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const payload = {
        id: executionData.id || crypto.randomUUID(),
        account_id: executionData.account_id,
        project_id: executionData.project_id,
        workflow_id: executionData.workflow_id,
        input_parameters: executionData.input_parameters || {},
        triggered_by_user_id: executionData.triggered_by_user_id || null,
        metadata: executionData.metadata || null
      };

      const response = await this.executionRequest('/executions/', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      const executionPayload = response?.body?.body || response?.body || response?.execution || response;
      const executionId = executionPayload?.execution_id || response?.execution_id || null;

      return {
        success: true,
        data: {
          execution: executionPayload,
          execution_id: executionId,
          request_id: executionPayload?.request_id || response?.body?.request_id || response?.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error starting execution:', error);
      return {
        success: false,
        error: error.message || 'Error starting workflow execution'
      };
    }
  }

  /**
   * List workflow executions with filtering
   * GET /executions/
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @param {string} [params.workflow_id] - Filter by workflow
   * @param {string} [params.agent_id] - Filter to runs of workflows associated with this agent
   * @param {Array<string>} [params.status_filter] - Filter by status
   * @param {Array<string>} [params.trigger_type_filter] - Filter by trigger type
   * @param {string} [params.start_date] - Filter after date
   * @param {string} [params.end_date] - Filter before date
   * @param {number} [params.limit=50] - Max results (1-100)
   * @param {number} [params.offset=0] - Skip results
   * @param {string} [params.sort_by='started_at'] - Sort field
   * @param {string} [params.sort_order='desc'] - Sort direction
   * @returns {Promise<Object>} response { success, data: { executions, total_count }, error }
   */
  async listExecutions(params = {}) {
    try {
      if (!params.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        account_id: params.account_id,
        project_id: params.project_id
      });

      if (params.workflow_id) queryParams.append('workflow_id', params.workflow_id);
      if (params.agent_id) queryParams.append('agent_id', params.agent_id);
      if (params.status_filter?.length) {
        params.status_filter.forEach(s => queryParams.append('status_filter', s));
      }
      if (params.trigger_type_filter?.length) {
        params.trigger_type_filter.forEach(t => queryParams.append('trigger_type_filter', t));
      }
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      // Búsqueda por nombre de workflow o etiqueta, resuelta en el servidor
      // (zenitailab !1053). Sustituye al `?tags=` de !1052, que ya no existe:
      // mandarlo ahora devuelve 400.
      if (params.search) queryParams.append('search', params.search);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      const response = await this.executionRequest(`/executions/?${queryParams}`);

      const pagination = response.body?.pagination || {};
      const statistics = response.body?.statistics || {};
      return {
        success: true,
        data: {
          executions: response.body?.executions || response.executions || [],
          // Server-computed rollups (counts, success_rate, avg_duration) and
          // pagination metadata — used by the activity By-agent cards so the
          // metrics come from the backend instead of being recomputed here.
          statistics,
          pagination,
          total_count: pagination.total ?? response.body?.total_count ?? response.total_count ?? 0,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing executions:', error);
      return {
        success: false,
        error: error.message || 'Error listing executions'
      };
    }
  }

  /**
   * Get execution details
   * GET /executions/{execution_id}
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @param {boolean} [params.include_step_results=true] - Include step results
   * @param {boolean} [params.include_logs_summary=true] - Include logs summary
   * @returns {Promise<Object>} response { success, data: { execution }, error }
   */
  async getExecution(execution_id, params = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        project_id: params.project_id,
        include_step_results: params.include_step_results !== false ? 'true' : 'false',
        include_logs_summary: params.include_logs_summary !== false ? 'true' : 'false',
      });

      const response = await this.executionRequest(`/executions/${execution_id}?${queryParams}`);

      return {
        success: true,
        data: {
          execution: response.body?.execution || response.execution || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting execution:', error);
      return {
        success: false,
        error: error.message || 'Error getting execution details'
      };
    }
  }

  /**
   * Get execution status
   * GET /executions/{execution_id}/status
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { status, progress }, error }
   */
  async getExecutionStatus(execution_id, params = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!params.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        account_id: params.account_id,
        project_id: params.project_id
      });

      const response = await this.executionRequest(`/executions/${execution_id}/status?${queryParams}`);

      return {
        success: true,
        data: {
          status: response.body?.status || response.status,
          progress: response.body?.progress || response.progress,
          execution: response.body?.execution || response.execution || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting execution status:', error);
      return {
        success: false,
        error: error.message || 'Error getting execution status'
      };
    }
  }

  /**
   * Get execution logs
   * GET /executions/{execution_id}/logs
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @param {string} [params.log_level] - Filter by log level
   * @param {string} [params.source] - Filter by log source
   * @param {string} [params.start_time] - Filter after time
   * @param {string} [params.end_time] - Filter before time
   * @param {number} [params.limit=100] - Max logs (1-1000)
   * @param {number} [params.offset=0] - Skip logs
   * @param {string} [params.sort_by='timestamp'] - Sort field
   * @param {boolean} [params.descending=true] - Sort descending
   * @returns {Promise<Object>} response { success, data: { logs, total_count }, error }
   */
  async getExecutionLogs(execution_id, params = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!params.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        account_id: params.account_id,
        project_id: params.project_id
      });

      if (params.log_level) queryParams.append('log_level', params.log_level);
      if (params.source) queryParams.append('source', params.source);
      if (params.start_time) queryParams.append('start_time', params.start_time);
      if (params.end_time) queryParams.append('end_time', params.end_time);
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.descending !== undefined) queryParams.append('descending', params.descending.toString());

      const response = await this.executionRequest(`/executions/${execution_id}/logs?${queryParams}`);

      // Parse the response body if it's a string
      const responseBody = typeof response.body === 'string' 
        ? JSON.parse(response.body) 
        : response.body || response;

      return {
        success: true,
        data: {
          logs: responseBody.logs || [],
          total_count: responseBody.total_count || 0,
          request_id: responseBody.request_id,
          execution: responseBody.execution,
          workflow: responseBody.workflow
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting execution logs:', error);
      return {
        success: false,
        error: error.message || 'Error getting execution logs'
      };
    }
  }

  /**
   * Cancel a running execution
   * POST /executions/{execution_id}/cancel
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} cancelData - Cancel parameters
   * @param {string} cancelData.account_id - Account ID (required)
   * @param {string} cancelData.project_id - Project ID (required)
   * @param {string} [cancelData.reason] - Reason for cancellation
   * @param {boolean} [cancelData.refund_coins=true] - Whether to refund coins
   * @returns {Promise<Object>} response { success, data: { execution }, error }
   */
  async cancelExecution(execution_id, cancelData = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!cancelData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!cancelData.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const payload = {
        id: cancelData.id || crypto.randomUUID(),
        account_id: cancelData.account_id,
        project_id: cancelData.project_id,
        execution_id: execution_id,
        reason: cancelData.reason || null,
      };

      const response = await this.executionRequest(`/executions/${execution_id}/cancel`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      // 202 = async ack — cancelling state set, terminal 'cancelled' arrives via stream/poll
      return {
        success: true,
        cancelling: true,
        data: {
          status_code: response.status_code,
          message: response.message,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      // 504 Gateway Timeout means the gateway timed out waiting for the backend,
      // but the cancel request was received and processed — treat as likely success.
      const isGatewayTimeout =
        error.message?.includes('504') ||
        error.message?.includes('Gateway Timeout') ||
        error.message?.includes('HTTP Error 504');

      if (isGatewayTimeout) {
        console.warn('WorkflowService: Cancel request timed out (504) but backend likely processed it.');
        return { success: true, timedOut: true };
      }

      console.error('WorkflowService: Error cancelling execution:', error);
      return {
        success: false,
        error: error.message || 'Error cancelling execution'
      };
    }
  }

  /**
   * Helper to format execution status for display
   * @param {string} status - Execution status
   * @returns {Object} { label, icon, color, bgColor }
   */
  formatExecutionStatus(status) {
    const statusMap = {
      'pending': { label: 'Pending', icon: '⏳', color: 'text-yellow-400', bgColor: 'bg-yellow-400/10' },
      'running': { label: 'Running', icon: '▶️', color: 'text-blue-400', bgColor: 'bg-blue-400/10' },
      'completed': { label: 'Completed', icon: '✅', color: 'text-green-400', bgColor: 'bg-green-400/10' },
      'failed': { label: 'Failed', icon: '❌', color: 'text-red-400', bgColor: 'bg-red-400/10' },
      'cancelled': { label: 'Cancelled', icon: '🚫', color: 'text-gray-400', bgColor: 'bg-gray-400/10' },
      'timeout': { label: 'Timeout', icon: '⏰', color: 'text-orange-400', bgColor: 'bg-orange-400/10' }
    };

    return statusMap[status?.toLowerCase()] || { 
      label: status || 'Unknown', 
      icon: '❓', 
      color: 'text-gray-400', 
      bgColor: 'bg-gray-400/10' 
    };
  }

  /**
   * Helper to format trigger type for display
   * @param {string} triggerType - Trigger type
   * @returns {Object} { label, icon, color }
   */
  formatTriggerType(triggerType) {
    const typeMap = {
      'manual': { label: 'Manual', icon: '🖱️', color: 'text-blue-400' },
      'scheduled': { label: 'Scheduled', icon: '⏰', color: 'text-purple-400' },
      'webhook': { label: 'Webhook', icon: '🔗', color: 'text-green-400' },
      'shopify': { label: 'Shopify', icon: '🛍️', color: 'text-emerald-400' },
      'whatsapp': { label: 'WhatsApp', icon: '💬', color: 'text-green-500' }
    };

    return typeMap[triggerType] || { label: triggerType || 'Unknown', icon: '❓', color: 'text-gray-400' };
  }

  // ============================================================================
  // ADDITIONAL WORKFLOW LIFECYCLE METHODS
  // ============================================================================

  /**
   * Publish a workflow (creates new version) - Protected endpoint
   * POST /workflows/{workflow_id}/publish
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} publishData - Publish parameters
   * @param {string} [publishData.notes] - Optional publish notes
   * @param {string} [publishData.version_notes] - Backward-compatible notes field
   * @returns {Promise<Object>} response { success, data: { workflow, version }, error }
   */
  async publishWorkflow(workflow_id, publishData = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const payload = {
        notes: publishData.notes ?? publishData.version_notes ?? null
      };

      const response = await this.request(`/workflows/${workflow_id}/publish`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          version: response.body?.version || response.version,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error publishing workflow:', error);
      return {
        success: false,
        error: error.message || 'Error publishing workflow'
      };
    }
  }

  /**
   * Rollback workflow to a previous version - Protected endpoint
   * POST /workflows/{workflow_id}/rollback
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} rollbackData - Rollback parameters
   * @param {string} rollbackData.account_id - Account ID (required)
   * @param {string} rollbackData.project_id - Project ID (required)
   * @param {string|number} rollbackData.target_version - Version to rollback to (required)
   * @param {string} [rollbackData.reason] - Reason for rollback
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async rollbackWorkflow(workflow_id, rollbackData = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!rollbackData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!rollbackData.project_id) {
        return { success: false, error: 'project_id is required' };
      }
      if (!rollbackData.target_version) {
        return { success: false, error: 'target_version is required' };
      }

      const payload = {
        account_id: rollbackData.account_id,
        project_id: rollbackData.project_id,
        workflow_id: workflow_id,
        target_version: rollbackData.target_version,
        reason: rollbackData.reason || null
      };

      const response = await this.request(`/workflows/${workflow_id}/rollback`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error rolling back workflow:', error);
      return {
        success: false,
        error: error.message || 'Error rolling back workflow'
      };
    }
  }

  /**
   * Discard unpublished draft changes - Protected endpoint
   * POST /workflows/{workflow_id}/discard-draft
   * @param {string} workflow_id - Workflow ID (required)
   * @returns {Promise<Object>} response { success, data: { success }, error }
   */
  async discardDraft(workflow_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const response = await this.request(`/workflows/${workflow_id}/discard-draft`, {
        method: 'POST'
      });

      return {
        success: true,
        data: {
          success: response.body?.success ?? true,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error discarding draft:', error);
      return {
        success: false,
        error: error.message || 'Error discarding draft'
      };
    }
  }

  /**
   * List workflow version history - Protected endpoint
   * GET /workflows/{workflow_id}/versions
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} params - Query parameters
   * @param {number} [params.limit=50] - Limit for pagination
   * @param {number} [params.offset=0] - Offset for pagination
   * @returns {Promise<Object>} response { success, data: { versions, total_count, current_published_version_number }, error }
   */
  async listWorkflowVersions(workflow_id, { limit = 50, offset = 0 } = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });

      const response = await this.request(`/workflows/${workflow_id}/versions?${params}`);

      return {
        success: true,
        data: {
          versions: response.body?.versions || [],
          total_count: response.body?.total_count || 0,
          current_published_version_number: response.body?.current_published_version_number,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing workflow versions:', error);
      return {
        success: false,
        error: error.message || 'Error listing workflow versions'
      };
    }
  }

  /**
   * Get a specific workflow version - Protected endpoint
   * GET /workflows/{workflow_id}/versions/{version_number}
   * @param {string} workflow_id - Workflow ID (required)
   * @param {number} version_number - Version number (required)
   * @returns {Promise<Object>} response { success, data: { version }, error }
   */
  async getWorkflowVersion(workflow_id, version_number) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!version_number) {
        return { success: false, error: 'version_number is required' };
      }

      const response = await this.request(`/workflows/${workflow_id}/versions/${version_number}`);

      return {
        success: true,
        data: {
          id: response.body?.id,
          version_number: response.body?.version_number,
          published_at: response.body?.published_at,
          published_by_user_id: response.body?.published_by_user_id,
          notes: response.body?.notes,
          snapshot: response.body?.snapshot,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting workflow version:', error);
      return {
        success: false,
        error: error.message || 'Error getting workflow version'
      };
    }
  }

  // ============================================================================
  // DEPLOYMENT STRATEGY METHODS (Shadow/Canary)
  // ============================================================================

  /**
   * Configure deployment strategy (shadow or canary) - Protected endpoint
   * POST /workflows/{workflow_id}/deployment-strategy
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} strategyData - Strategy configuration
   * @param {string} strategyData.account_id - Account ID (required)
   * @param {string} strategyData.project_id - Project ID (required)
   * @param {Object} strategyData.strategy - Strategy object (required)
   * @param {string} strategyData.strategy.type - 'shadow' or 'canary' (required)
   * @param {string} strategyData.strategy.candidate_version_id - Candidate version UUID (required)
   * @param {number} [strategyData.strategy.auto_rollback_threshold] - Auto rollback threshold (%)
   * @param {number} [strategyData.strategy.sampling_rate] - Sampling rate for shadow (%)
   * @param {number} [strategyData.strategy.traffic_percentage] - Traffic % for canary
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async configureDeploymentStrategy(workflow_id, strategyData) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!strategyData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!strategyData.project_id) {
        return { success: false, error: 'project_id is required' };
      }
      if (!strategyData.strategy) {
        return { success: false, error: 'strategy is required' };
      }

      const payload = {
        id: strategyData.id || this.generateRequestId(),
        account_id: strategyData.account_id,
        project_id: strategyData.project_id,
        strategy: strategyData.strategy
      };

      const response = await this.request(`/workflows/${workflow_id}/deployment-strategy`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error configuring deployment strategy:', error);
      return {
        success: false,
        error: error.message || 'Error configuring deployment strategy'
      };
    }
  }

  /**
   * Get current deployment strategy - Protected endpoint
   * GET /workflows/{workflow_id}/deployment-strategy
   * @param {string} workflow_id - Workflow ID (required)
   * @param {string} project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { strategy }, error }
   */
  async getDeploymentStrategy(workflow_id, project_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const params = new URLSearchParams({ project_id });
      const response = await this.request(`/workflows/${workflow_id}/deployment-strategy?${params}`);

      return {
        success: true,
        data: {
          strategy: response.body?.strategy || null,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting deployment strategy:', error);
      return {
        success: false,
        error: error.message || 'Error getting deployment strategy'
      };
    }
  }

  /**
   * Delete deployment strategy - Protected endpoint
   * DELETE /workflows/{workflow_id}/deployment-strategy
   * @param {string} workflow_id - Workflow ID (required)
   * @param {string} project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { cleared }, error }
   */
  async deleteDeploymentStrategy(workflow_id, project_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const params = new URLSearchParams({ project_id });
      const response = await this.request(`/workflows/${workflow_id}/deployment-strategy?${params}`, {
        method: 'DELETE'
      });

      return {
        success: true,
        data: {
          cleared: response.body?.cleared ?? true,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error deleting deployment strategy:', error);
      return {
        success: false,
        error: error.message || 'Error deleting deployment strategy'
      };
    }
  }

  /**
   * Promote candidate version to published - Protected endpoint
   * POST /workflows/{workflow_id}/deployment-strategy/promote
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} promoteData - Promotion data
   * @param {string} promoteData.account_id - Account ID (required)
   * @param {string} promoteData.project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async promoteCandidate(workflow_id, promoteData) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!promoteData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!promoteData.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const payload = {
        id: promoteData.id || this.generateRequestId(),
        account_id: promoteData.account_id,
        project_id: promoteData.project_id
      };

      const response = await this.request(`/workflows/${workflow_id}/deployment-strategy/promote`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error promoting candidate:', error);
      return {
        success: false,
        error: error.message || 'Error promoting candidate version'
      };
    }
  }

  /**
   * Get deployment statistics - Protected endpoint
   * GET /workflows/{workflow_id}/deployment-strategy/stats
   * @param {string} workflow_id - Workflow ID (required)
   * @param {string} project_id - Project ID (required)
   * @param {number} [window_hours=24] - Rolling window in hours (max 720)
   * @returns {Promise<Object>} response { success, data: { stats }, error }
   */
  async getDeploymentStats(workflow_id, project_id, window_hours = 24) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const params = new URLSearchParams({
        project_id,
        window_hours: window_hours.toString()
      });

      const response = await this.request(
        `/workflows/${workflow_id}/deployment-strategy/stats?${params}`
      );

      return {
        success: true,
        data: {
          stats: response.body?.stats || response.stats || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting deployment stats:', error);
      return {
        success: false,
        error: error.message || 'Error getting deployment statistics'
      };
    }
  }

  // ============================================================================
  // TEST PINS METHODS
  // ============================================================================

  /**
   * List all test pins on a workflow - Protected endpoint
   * GET /workflows/{workflow_id}/test-pins
   * @param {string} workflow_id - Workflow ID (required)
   * @returns {Promise<Object>} response { success, data: { pins }, error }
   */
  async listTestPins(workflow_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const response = await this.request(`/workflows/${workflow_id}/test-pins`);

      return {
        success: true,
        data: {
          pins: response.body?.pins || [],
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing test pins:', error);
      return {
        success: false,
        error: error.message || 'Error listing test pins'
      };
    }
  }

  /**
   * Get a single test pin - Protected endpoint
   * GET /workflows/{workflow_id}/test-pins/{step_id}
   * @param {string} workflow_id - Workflow ID (required)
   * @param {string} step_id - Step ID (required)
   * @returns {Promise<Object>} response { success, data: { pin }, error }
   */
  async getTestPin(workflow_id, step_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!step_id) {
        return { success: false, error: 'step_id is required' };
      }

      const response = await this.request(`/workflows/${workflow_id}/test-pins/${step_id}`);

      return {
        success: true,
        data: {
          pin: response.body?.pin || response.pin || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting test pin:', error);
      return {
        success: false,
        error: error.message || 'Error getting test pin'
      };
    }
  }

  /**
   * Upsert a test pin (idempotent) - Protected endpoint
   * PUT /workflows/{workflow_id}/test-pins/{step_id}
   * @param {string} workflow_id - Workflow ID (required)
   * @param {string} step_id - Step ID (required)
   * @param {Object} pinData - Pin data
   * @param {Object} pinData.pinned_output_data - Step output to replay (required)
   * @returns {Promise<Object>} response { success, data: { pin }, error }
   */
  async upsertTestPin(workflow_id, step_id, pinData) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!step_id) {
        return { success: false, error: 'step_id is required' };
      }
      if (!pinData.pinned_output_data) {
        return { success: false, error: 'pinned_output_data is required' };
      }

      const payload = {
        id: pinData.id || this.generateRequestId(),
        pinned_output_data: pinData.pinned_output_data
      };

      const response = await this.request(`/workflows/${workflow_id}/test-pins/${step_id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          pin: response.body?.pin || response.pin || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error upserting test pin:', error);
      return {
        success: false,
        error: error.message || 'Error upserting test pin'
      };
    }
  }

  /**
   * Delete a test pin - Protected endpoint
   * DELETE /workflows/{workflow_id}/test-pins/{step_id}
   * @param {string} workflow_id - Workflow ID (required)
   * @param {string} step_id - Step ID (required)
   * @returns {Promise<Object>} response { success, error }
   */
  async deleteTestPin(workflow_id, step_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!step_id) {
        return { success: false, error: 'step_id is required' };
      }

      await this.request(`/workflows/${workflow_id}/test-pins/${step_id}`, {
        method: 'DELETE'
      });

      return { success: true };
    } catch (error) {
      // Backend already lost the pin (orphaned by workflow update or never existed):
      // treat as successful cleanup so UI can resync its local set.
      if (error.status === 404 || error.code === 'TEST_PIN_NOT_FOUND') {
        return { success: true, alreadyMissing: true };
      }
      console.error('WorkflowService: Error deleting test pin:', error);
      return {
        success: false,
        error: error.message || 'Error deleting test pin'
      };
    }
  }

  /**
   * Create a pin by lifting step output from a prior execution - Protected endpoint
   * POST /workflows/{workflow_id}/test-pins/{step_id}/from-execution
   * @param {string} workflow_id - Workflow ID (required)
   * @param {string} step_id - Step ID (required)
   * @param {Object} sourceData - Source execution data
   * @param {string} sourceData.execution_id - Source execution UUID (required)
   * @returns {Promise<Object>} response { success, data: { pin }, error }
   */
  async createTestPinFromExecution(workflow_id, step_id, sourceData) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!step_id) {
        return { success: false, error: 'step_id is required' };
      }
      if (!sourceData.execution_id) {
        return { success: false, error: 'execution_id is required' };
      }

      const payload = {
        id: sourceData.id || this.generateRequestId(),
        execution_id: sourceData.execution_id
      };

      const response = await this.request(
        `/workflows/${workflow_id}/test-pins/${step_id}/from-execution`,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        }
      );

      return {
        success: true,
        data: {
          pin: response.body?.pin || response.pin || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error creating test pin from execution:', error);
      return {
        success: false,
        error: error.message || 'Error creating test pin from execution'
      };
    }
  }

  /**
   * Get the steps catalog (JSON schema for all step types)
   * GET /steps/catalog
   * @returns {Promise<Object>} response { success, data: { catalog }, error }
   */
  async getStepsCatalog() {
    try {
      const response = await this.request('/steps/catalog');

      return {
        success: true,
        data: {
          catalog: response.body?.catalog || response.catalog || response,
          step_types: response.body?.step_types || response.step_types,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting steps catalog:', error);
      return {
        success: false,
        error: error.message || 'Error getting steps catalog'
      };
    }
  }

  /**
   * Validate cron expression for scheduled trigger
   * POST /triggers/scheduled/validate-cron
   * @param {Object} cronData - Cron validation data
   * @param {string} cronData.cron_expression - Cron expression to validate (required)
   * @param {string} [cronData.timezone='UTC'] - Timezone for the cron
   * @returns {Promise<Object>} response { success, data: { valid, next_runs }, error }
   */
  async validateCronExpression(cronData) {
    try {
      if (!cronData.cron_expression) {
        return { success: false, error: 'cron_expression is required' };
      }

      const payload = {
        cron_expression: cronData.cron_expression,
        timezone: cronData.timezone || 'UTC'
      };

      const response = await this.request('/triggers/scheduled/validate-cron', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          valid: response.body?.valid ?? response.valid ?? false,
          next_runs: response.body?.next_runs || response.next_runs || [],
          error_message: response.body?.error_message || response.error_message,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error validating cron expression:', error);
      return {
        success: false,
        error: error.message || 'Error validating cron expression'
      };
    }
  }

  // ============================================================================
  // ADDITIONAL EXECUTION METHODS
  // ============================================================================

  /**
   * Get execution result (polling-friendly)
   * GET /executions/{execution_id}/result
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { completed, result }, error }
   */
  async getExecutionResult(execution_id, params = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        project_id: params.project_id
      });

      const response = await this.executionRequest(`/executions/${execution_id}/result?${queryParams}`);

      return {
        success: true,
        data: {
          completed: response.body?.completed ?? response.completed ?? false,
          result: response.body?.result || response.result,
          status: response.body?.status || response.status,
          execution: response.body?.execution || response.execution,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting execution result:', error);
      return {
        success: false,
        error: error.message || 'Error getting execution result'
      };
    }
  }

  /**
   * Get the ordered history of an execution
   * GET /executions/{execution_id}/timeline
   *
   * One entry per *run of a step*, interleaved with the waits, in the order
   * things happened. A step reached twice through a back-edge appears twice,
   * each pass with its own timings — which is why this replaces reading
   * `step_progress`, where the second pass overwrites the first.
   *
   * `input` and `output` arrive trimmed when large; `getTimelineEntry` has
   * the whole values.
   *
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} params - Query parameters
   * @param {string} params.project_id - Project ID (required)
   * @param {number} [params.limit=200] - Entries per page
   * @param {number} [params.offset=0] - Entry to start from
   * @returns {Promise<Object>} response { success, data, error }
   */
  async getExecutionTimeline(execution_id, params = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        project_id: params.project_id,
        limit: String(params.limit ?? 200),
        offset: String(params.offset ?? 0)
      });

      const response = await this.executionRequest(`/executions/${execution_id}/timeline?${queryParams}`);
      const body = response.body || response;

      return {
        success: true,
        data: {
          execution_id: body.execution_id,
          timeline: body.timeline || [],
          not_executed: body.not_executed || [],
          counters: body.counters || null,
          duration: body.duration || null,
          // Executions that ran before attempt recording existed rebuild the
          // timeline from step_progress: fewer entries, no inputs, and no
          // duration where none was measured. The view says so instead of
          // implying precision.
          degraded: body.degraded ?? false,
          degraded_reason: body.degraded_reason || null,
          truncated: body.truncated ?? false,
          truncated_reason: body.truncated_reason || null,
          pagination: body.pagination || null,
          request_id: body.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting execution timeline:', error);
      return {
        success: false,
        error: error.message || 'Error getting execution timeline'
      };
    }
  }

  /**
   * Get one timeline entry with nothing trimmed
   * GET /executions/{execution_id}/timeline/{seq}
   * @param {string} execution_id - Execution ID (required)
   * @param {number} seq - Position of the entry in the timeline, 1-based (required)
   * @param {Object} params - Query parameters
   * @param {string} params.project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { entry }, error }
   */
  async getTimelineEntry(execution_id, seq, params = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      // A seq of 0 is answered 422 by the API, and reads as a bug here first.
      if (!Number.isInteger(seq) || seq < 1) {
        return { success: false, error: 'seq must be an integer of 1 or more' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        project_id: params.project_id
      });

      const response = await this.executionRequest(
        `/executions/${execution_id}/timeline/${seq}?${queryParams}`
      );
      const body = response.body || response;

      return {
        success: true,
        data: {
          execution_id: body.execution_id,
          entry: body.entry || null,
          request_id: body.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting timeline entry:', error);
      return {
        success: false,
        error: error.message || 'Error getting timeline entry'
      };
    }
  }

  /**
   * Get the graph an execution actually ran against
   * GET /executions/{execution_id}/graph
   *
   * The frozen snapshot of the version the run was pinned to, not the living
   * workflow: a published workflow keeps being edited after a run finishes.
   * `version.source` says which graph came back when the pin did not resolve.
   *
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} params - Query parameters
   * @param {string} params.project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { steps, edges, version }, error }
   */
  async getExecutionGraph(execution_id, params = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        project_id: params.project_id
      });

      const response = await this.executionRequest(`/executions/${execution_id}/graph?${queryParams}`);
      const body = response.body || response;

      return {
        success: true,
        data: {
          execution_id: body.execution_id,
          workflow: body.workflow || null,
          version: body.version || null,
          steps: body.steps || [],
          // Empty on versions published before the canvas stored its edges.
          // steps[].dependencies is the topology that is always there.
          edges: body.edges || [],
          request_id: body.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting execution graph:', error);
      return {
        success: false,
        error: error.message || 'Error getting execution graph'
      };
    }
  }

  /**
   * Get SSE stream URL for real-time execution updates
   * @param {string} execution_id - Execution ID (required)
   * @param {string} project_id - Project ID (required)
   * @returns {string} SSE stream URL
   */
  getExecutionStreamURL(execution_id, project_id) {
    if (!execution_id || !project_id) {
      throw new Error('execution_id and project_id are required');
    }
    return `${this.executionURL}/executions/${execution_id}/stream?project_id=${project_id}`;
  }

  /**
   * Create a streaming connection for execution updates with proper authentication
   * Uses fetch with ReadableStream since EventSource doesn't support custom headers
   * @param {string} execution_id - Execution ID (required)
   * @param {string} project_id - Project ID (required)
   * @param {Function} onMessage - Callback for SSE messages
   * @param {Function} onError - Callback for errors
   * @param {Function} [onClose] - Callback fired when the server cleanly closes the stream
   * @returns {Object} Controller with abort() method to stop the stream
   */
  async streamExecutionUpdates(execution_id, project_id, onMessage, onError, onClose) {
    const url = this.getExecutionStreamURL(execution_id, project_id);
    const abortController = new AbortController();
    
    // Get JWT token for authentication
    let token = AuthService.getAccessToken();
    
    // Check if token is expired
    if (token && AuthService.isTokenExpired()) {
      logger.warn('JWT token is expired for SSE stream');
      AuthService.logout();
      token = null;
    }
    
    // Prepare headers with JWT
    const headers = {
      'Accept': 'text/event-stream',
      'Cache-Control': 'no-cache',
    };
    
    // Add JWT if available and not expired, otherwise fallback to API key
    if (token && !AuthService.isTokenExpired()) {
      headers.Authorization = `Bearer ${token}`;
    } else {
      const apiKey = this.getFallbackApiKey();
      if (apiKey) {
        headers['X-Zenit-Api-Key'] = apiKey;
      }
    }
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: abortController.signal,
      });
      
      if (!response.ok) {
        throw new Error(`Stream connection failed: ${response.status} ${response.statusText}`);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      // Read the stream
      const readStream = async () => {
        try {
          console.log('📡 SSE Stream reader started');
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.log('📡 SSE Stream ended (done=true)');
              if (onClose) onClose();
              break;
            }
            
            // Decode the chunk and add to buffer
            const chunk = decoder.decode(value, { stream: true });
            console.log('📦 SSE Chunk received:', chunk);
            buffer += chunk;
            
            // SSE messages are separated by double newlines (empty line)
            // But we need to handle single \n as line separator within a message
            const lines = buffer.split('\n');
            
            // Process complete messages
            let currentMessage = [];
            for (let i = 0; i < lines.length; i++) {
              const line = lines[i];
              
              // Empty line marks end of message
              if (line.trim() === '') {
                if (currentMessage.length > 0) {
                  // Process this complete message
                  const messageText = currentMessage.join('\n');
                  console.log('📩 Raw SSE message:', messageText);
                  
                  // Parse SSE message format
                  const event = {
                    id: null,
                    event: null,
                    data: null,
                  };
                  
                  for (const rawLine of currentMessage) {
                    // Strip trailing \r (servers using CRLF line endings).
                    const msgLine = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
                    if (msgLine.startsWith(':')) continue;
                    const colonIdx = msgLine.indexOf(':');
                    if (colonIdx === -1) continue;
                    const field = msgLine.slice(0, colonIdx);
                    const value = colonIdx + 1 < msgLine.length && msgLine[colonIdx + 1] === ' '
                      ? msgLine.slice(colonIdx + 2)
                      : msgLine.slice(colonIdx + 1);
                    if (field === 'id') event.id = value;
                    else if (field === 'event') event.event = value;
                    else if (field === 'data') event.data = value;
                  }
                  
                  console.log('🎯 Parsed SSE event:', event);
                  
                  // Only process if we have data
                  if (event.data && event.data.trim()) {
                    try {
                      const parsed = JSON.parse(event.data);
                      console.log('✅ SSE data parsed:', parsed);
                      
                      // Map backend event_type to frontend format with step_id and status
                      const mappedEvent = {
                        execution_id: parsed.execution_id,
                        step_id: parsed.step_id || null,
                        status: null,
                        event_type: parsed.event_type,
                        timestamp: parsed.timestamp
                      };
                      
                      // Map event_type to status
                      if (parsed.event_type === 'step_started') {
                        mappedEvent.status = 'running';
                      } else if (parsed.event_type === 'step_completed') {
                        mappedEvent.status = 'completed';
                      } else if (parsed.event_type === 'step_failed') {
                        mappedEvent.status = 'failed';
                      } else if (parsed.event_type === 'execution_completed') {
                        mappedEvent.status = 'completed';
                      } else if (parsed.event_type === 'execution_failed') {
                        mappedEvent.status = 'failed';
                      }
                      
                      console.log('🔄 Mapped event:', mappedEvent);
                      
                      logger.debug('SSE event received:', event.event, mappedEvent);
                      onMessage(mappedEvent);
                    } catch (err) {
                      console.error('❌ Failed to parse SSE JSON:', err, event.data);
                      logger.error('Failed to parse SSE data:', err, event.data);
                    }
                  } else {
                    console.warn('⚠️ SSE event has no data:', event);
                  }
                  
                  // Reset for next message
                  currentMessage = [];
                }
              } else {
                // Add line to current message
                currentMessage.push(line);
              }
            }
            
            // Keep incomplete message in buffer
            if (currentMessage.length > 0) {
              buffer = currentMessage.join('\n') + '\n';
            } else {
              buffer = '';
            }
          }
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('❌ Stream reading error:', err);
            logger.error('Stream reading error:', err);
            if (onError) onError(err);
          }
        }
      };
      
      // Start reading the stream
      readStream();
      
    } catch (err) {
      logger.error('Failed to establish stream connection:', err);
      if (onError) onError(err);
    }
    
    // Return controller to allow external abort
    return {
      abort: () => abortController.abort(),
    };
  }

  /**
   * Test execution (for draft workflows) - Protected endpoint
   * POST /test-executions
   * @param {Object} testData - Test execution parameters
   * @param {string} testData.account_id - Account ID (required)
   * @param {string} testData.project_id - Project ID (required)
   * @param {string} testData.workflow_id - Workflow ID (must be draft) (required)
   * @param {Object} [testData.input_parameters] - Input parameters for test
   * @param {Object} [testData.metadata] - Optional test metadata
   * @returns {Promise<Object>} response { success, data: { execution }, error }
   */
  async testExecution(testData) {
    try {
      if (!testData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!testData.project_id) {
        return { success: false, error: 'project_id is required' };
      }
      if (!testData.workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const payload = {
        id: testData.id || crypto.randomUUID(),
        account_id: testData.account_id,
        project_id: testData.project_id,
        workflow_id: testData.workflow_id,
        input_parameters: testData.input_parameters || {},
        metadata: testData.metadata || { test_mode: true }
      };

      const response = await this.executionRequest('/test-executions', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      const executionPayload = response?.body?.body || response?.body || response?.execution || response;
      const executionId = executionPayload?.execution_id || response?.execution_id || null;

      return {
        success: true,
        data: {
          execution: executionPayload,
          execution_id: executionId,
          request_id: executionPayload?.request_id || response?.body?.request_id || response?.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error starting test execution:', error);
      return {
        success: false,
        error: error.message || 'Error starting test execution'
      };
    }
  }

  /**
   * Resume a paused workflow by token - No auth required
   * POST /webhooks/resume/{token}
   * @param {string} token - Resume token (required)
   * @param {Object} [resumeData] - Optional resume data
   * @returns {Promise<Object>} response { success, data: { execution }, error }
   */
  async resumeWorkflowByToken(token, resumeData = {}) {
    try {
      if (!token) {
        return { success: false, error: 'token is required' };
      }

      const payload = {
        resume_data: resumeData.resume_data || null
      };

      // Note: This endpoint doesn't require authentication
      const response = await this.executionRequest(`/webhooks/resume/${token}`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {} // No auth headers
      });

      return {
        success: true,
        data: {
          execution: response.body?.execution || response.execution || response,
          resumed: response.body?.resumed ?? response.resumed ?? true,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error resuming workflow:', error);
      return {
        success: false,
        error: error.message || 'Error resuming workflow'
      };
    }
  }
}

const workflowService = new WorkflowService();
export default workflowService;
